import { useEffect, useRef, useState, type ChangeEvent, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { Icon, ICONE_SITUACAO } from "../../icons/Icon";
import { Aviso, Carregando } from "../../components/Layout";
import { api, fmt, type Consulta as InscricaoDetalhe, type ErroApi } from "../../lib/api";

export function DetalheInscricao() {
  const { id = "" } = useParams();
  const [dados, setDados] = useState<InscricaoDetalhe | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  useEffect(() => {
    api.get<InscricaoDetalhe>(`/api/candidato/inscricoes/${id}`)
      .then(setDados)
      .catch(() => setErro("Não foi possível carregar esta inscrição."));
  }, [id]);

  if (erro) return <Aviso tipo="erro">{erro}</Aviso>;
  if (!dados) return <Carregando texto="Carregando a inscrição" />;

  const icone = ICONE_SITUACAO[dados.situacao] ?? "circulo";

  return (
    <div className="pilha pilha-6">
      <div className="tc-cartao">
        <div className="linha linha-entre linha-quebra" style={{ marginBottom: 12 }}>
          <strong style={{ fontSize: 18 }}>{dados.cargo}</strong>
          <span className={`tc-badge tc-badge-${dados.situacao.toLowerCase()}`}>
            <Icon nome={icone} tamanho={14} />
            {" "}{dados.situacao.replace("_", " ").toLowerCase()}
          </span>
        </div>

        <p>{fmt.semRefs(dados.situacaoDescricao) || dados.situacaoDescricao}</p>

        {dados.motivoIndeferimento && (
          <Aviso tipo="erro" titulo="Motivo do indeferimento">
            {fmt.semRefs(dados.motivoIndeferimento) || "Procure a SEMED em caso de dúvida."}
          </Aviso>
        )}

        <dl className="grade grade-2" style={{ margin: "16px 0 0" }}>
          <div>
            <dt className="tc-ajuda">Componente / função</dt>
            <dd style={{ margin: 0 }}>{dados.componente ?? "Não se aplica"}</dd>
          </div>
          <div>
            <dt className="tc-ajuda">Enviada em</dt>
            <dd style={{ margin: 0 }} className="num">{fmt.dataHora(dados.enviadaEm)}</dd>
          </div>
        </dl>
      </div>

      <Foto id={id} dados={dados} aoAtualizar={setDados} />

      {dados.posicoes.length > 0 && (
        <div className="tc-cartao">
          <strong>Posição nas listas</strong>
          <div className="pilha pilha-2" style={{ marginTop: 12 }}>
            {dados.posicoes.map((p) => (
              <div key={p.lista} className="linha linha-entre">
                <span>{p.listaRotulo}</span>
                <strong className="num">{p.posicao} de {p.total}</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {dados.convocacoes.length > 0 && (
        <div className="tc-cartao">
          <strong>Convocações</strong>
          <div className="pilha pilha-2" style={{ marginTop: 12 }}>
            {dados.convocacoes.map((c) => (
              <div key={c.numero} className="pilha pilha-2">
                <div className="linha linha-entre linha-quebra">
                  <strong>{c.titulo}</strong>
                  <span className="tc-badge">{c.etapa}ª etapa</span>
                </div>
                <p className="texto-sm">
                  <Icon nome="calendario" tamanho={16} /> {fmt.dataHora(c.dataEvento)} · {c.localEvento}
                </p>
                <p className="tc-ajuda">Posição {c.posicaoGeral} na Classificação Geral.</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="tc-cartao">
        <strong>Histórico</strong>
        <div className="pilha pilha-2" style={{ marginTop: 12 }}>
          {[...dados.historico].reverse().map((m, i) => (
            <div key={i}>
              <p style={{ fontWeight: 600, margin: 0 }}>{m.titulo}</p>
              <p className="tc-ajuda num">{fmt.dataHora(m.quando)}</p>
              {m.detalhe && <p className="texto-sm">{m.detalhe}</p>}
            </div>
          ))}
        </div>
      </div>

      <div className="linha linha-quebra">
        <a className="tc-btn tc-btn-outline" href={`/api/candidato/inscricoes/${id}/comprovante`}>
          <Icon nome="baixar" tamanho={18} />
          Baixar comprovante
        </a>
      </div>

      {dados.recursoDisponivel && <Recurso id={id} />}
    </div>
  );
}

function Foto({ id, dados, aoAtualizar }: {
  id: string; dados: InscricaoDetalhe; aoAtualizar: (d: InscricaoDetalhe) => void;
}) {
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [versao, setVersao] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  async function selecionar(ev: ChangeEvent<HTMLInputElement>) {
    const arquivo = ev.target.files?.[0];
    ev.target.value = "";
    if (!arquivo) return;

    setErro(null);
    setEnviando(true);
    try {
      const dadosForm = new FormData();
      dadosForm.append("foto", arquivo);
      await api.postForm(`/api/candidato/inscricoes/${id}/foto`, dadosForm);
      aoAtualizar({ ...dados, fotoEnviada: true });
      setVersao((v) => v + 1);
    } catch (e) {
      setErro((e as ErroApi).mensagem);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <div className="tc-cartao">
      <strong>Foto</strong>
      <p className="tc-ajuda" style={{ marginTop: 4 }}>
        Usada na ficha de identificação da 2ª etapa. Formato JPG ou PNG.
      </p>

      {dados.fotoEnviada && (
        <img key={versao} src={`/api/candidato/inscricoes/${id}/foto?v=${versao}`}
             alt="Foto enviada para a inscrição"
             style={{ width: 120, height: 120, objectFit: "cover", borderRadius: 18, marginTop: 12 }} />
      )}

      {erro && <p className="tc-erro" role="alert">{erro}</p>}

      <div className="linha linha-quebra" style={{ marginTop: 12 }}>
        <button type="button" className="tc-btn tc-btn-outline" disabled={enviando}
                onClick={() => inputRef.current?.click()}>
          {enviando ? "Enviando..." : dados.fotoEnviada ? "Trocar foto" : "Enviar foto"}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png"
               onChange={selecionar} style={{ display: "none" }} />
      </div>
    </div>
  );
}

function Recurso({ id }: { id: string }) {
  const [aberto, setAberto] = useState(false);
  const [tipo, setTipo] = useState<"CLASSIFICACAO" | "ETAPA_2" | "ETAPA_3">("CLASSIFICACAO");
  const [justificativa, setJustificativa] = useState("");
  const [anexo, setAnexo] = useState<File | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [feito, setFeito] = useState<{ protocolo: string; mensagem: string } | null>(null);

  async function enviar(ev: FormEvent) {
    ev.preventDefault();
    if (justificativa.trim().length < 20) {
      setErro("Descreva a razão do recurso com pelo menos 20 caracteres.");
      return;
    }
    setEnviando(true);
    setErro(null);
    try {
      const dados = new FormData();
      dados.set("tipo", tipo);
      dados.set("justificativa", justificativa.trim());
      if (anexo) dados.set("anexo", anexo);
      setFeito(await api.postForm(`/api/candidato/inscricoes/${id}/recursos`, dados));
    } catch (e) {
      setErro((e as ErroApi).motivos[0] ?? (e as ErroApi).mensagem);
    } finally {
      setEnviando(false);
    }
  }

  if (feito) {
    return (
      <Aviso tipo="sucesso" titulo={`Recurso protocolado · ${feito.protocolo}`}>
        {feito.mensagem}
      </Aviso>
    );
  }

  if (!aberto) {
    return (
      <button type="button" className="tc-btn tc-btn-primario" onClick={() => setAberto(true)}>
        <Icon nome="arquivo" tamanho={18} />
        Apresentar recurso
      </button>
    );
  }

  return (
    <form onSubmit={enviar} className="tc-cartao pilha">
      <strong>Apresentar recurso</strong>
      {erro && <p className="tc-erro" role="alert">{erro}</p>}

      <div className="tc-campo">
        <label htmlFor="tipo-recurso">Tipo</label>
        <select id="tipo-recurso" value={tipo} onChange={(e) => setTipo(e.target.value as typeof tipo)}>
          <option value="CLASSIFICACAO">Resultado da classificação</option>
          <option value="ETAPA_2">2ª etapa: comprovação de documentos</option>
          <option value="ETAPA_3">3ª etapa: formalização do contrato</option>
        </select>
      </div>

      <div className="tc-campo">
        <label htmlFor="justificativa">Justificativa</label>
        <textarea id="justificativa" rows={5} value={justificativa}
                  onChange={(e) => setJustificativa(e.target.value)}
                  style={{ borderRadius: 18, background: "var(--tc-canvas)", border: "1px solid transparent", padding: 10, fontFamily: "inherit" }} />
      </div>

      <div className="tc-campo">
        <label htmlFor="anexo">Anexo em PDF (opcional)</label>
        <input id="anexo" type="file" accept="application/pdf"
               onChange={(e) => setAnexo(e.target.files?.[0] ?? null)} />
      </div>

      <div className="linha linha-quebra">
        <button type="submit" className="tc-btn tc-btn-primario" disabled={enviando}>
          {enviando ? "Enviando..." : "Protocolar recurso"}
        </button>
        <button type="button" className="tc-btn tc-btn-outline" onClick={() => setAberto(false)}>
          Cancelar
        </button>
      </div>
    </form>
  );
}
